import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Network, User, Building2, Zap, TrendingUp, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PageLoader } from '@shared/components/PageLoader';
import { connectsAPI } from '@/utils/supabase/client';
import type { ConnectsDashboardData, ConnectsSignal } from '@/types/connects';
import { formatDistanceToNow } from 'date-fns';

function importanceColor(importance: number): string {
  if (importance >= 5) return 'bg-destructive/10 text-destructive';
  if (importance >= 4) return 'bg-warning/10 text-warning';
  if (importance >= 3) return 'bg-warning/10 text-warning';
  return 'bg-muted text-muted-foreground';
}

function SignalCard({ signal }: { signal: ConnectsSignal }) {
  return (
    <div className="flex items-start gap-3 py-3 border-b last:border-0">
      <span className={`text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${importanceColor(signal.importance)}`}>
        {signal.importance}
      </span>
      <div className="min-w-0">
        <p className="text-sm leading-snug">{signal.summary}</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {formatDistanceToNow(new Date(signal.occurred_at), { addSuffix: true })}
          {signal.signal_type && <span className="ml-2 opacity-60">{signal.signal_type.replace(/_/g, ' ')}</span>}
        </p>
      </div>
    </div>
  );
}

export function ConnectsDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState<ConnectsDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    connectsAPI.getDashboard()
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <PageLoader />;

  if (error) {
    return (
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        <div className="rounded-md border border-destructive/50 bg-destructive/5 p-4 text-sm text-destructive">{error}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6 overflow-auto">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2 font-serif">
            <Network className="h-6 w-6" />
            Connects
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Shared intelligence — relationships, people, context</p>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <User className="h-8 w-8 text-muted-foreground/60" />
            <div>
              <p className="text-2xl font-bold font-serif">{data?.top_contacts?.length ?? 0}</p>
              <p className="text-xs text-muted-foreground">Active contacts</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Building2 className="h-8 w-8 text-muted-foreground/60" />
            <div>
              <p className="text-2xl font-bold font-serif">{data?.top_companies?.length ?? 0}</p>
              <p className="text-xs text-muted-foreground">Companies tracked</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Zap className="h-8 w-8 text-muted-foreground/60" />
            <div>
              <p className="text-2xl font-bold font-serif">{data?.recent_signals?.length ?? 0}</p>
              <p className="text-xs text-muted-foreground">Signals (30d)</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <TrendingUp className="h-8 w-8 text-muted-foreground/60" />
            <div>
              <p className="text-2xl font-bold font-serif">{data?.new_this_week?.length ?? 0}</p>
              <p className="text-xs text-muted-foreground">New this week</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top-of-mind contacts */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <User className="h-4 w-4" />
              Top of mind
              <span className="text-xs font-normal text-muted-foreground ml-1">last 7 days</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {!data?.top_contacts?.length ? (
              <p className="text-sm text-muted-foreground">No contacts mentioned yet</p>
            ) : (
              <div className="space-y-1">
                {data.top_contacts.map((ct) => (
                  <button
                    key={ct.id}
                    onClick={() => navigate(`/connects/contacts/${ct.id}`)}
                    className="w-full flex items-center justify-between gap-2 px-2 py-2 rounded-md hover:bg-accent text-left transition-colors"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{ct.display_name}</p>
                      {ct.companies?.name && (
                        <p className="text-xs text-muted-foreground truncate">{ct.companies.name}</p>
                      )}
                    </div>
                    <Badge variant="secondary" className="shrink-0 text-xs">{ct.mention_count}</Badge>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent signals */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Zap className="h-4 w-4" />
              Recent signals
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {!data?.recent_signals?.length ? (
              <p className="text-sm text-muted-foreground">No signals yet — start by posting to #knox</p>
            ) : (
              data.recent_signals.map((s, i) => <SignalCard key={i} signal={s} />)
            )}
          </CardContent>
        </Card>
      </div>

      {/* New this week */}
      {(data?.new_this_week?.length ?? 0) > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              New this week
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex flex-wrap gap-2">
              {data!.new_this_week.map((ct) => (
                <button
                  key={ct.id}
                  onClick={() => navigate(`/connects/contacts/${ct.id}`)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm hover:bg-accent transition-colors"
                >
                  <User className="h-3 w-3" />
                  {ct.display_name}
                  {ct.companies?.name && (
                    <span className="text-muted-foreground">· {ct.companies.name}</span>
                  )}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
